import * as qz from 'qz-tray'
import { hextorstr, KEYUTIL, KJUR, stob64 } from 'jsrsasign'
import { QZ_TRAY_CERT, QZ_TRAY_PRIVATE_KEY } from '@/pages/return/rgr11V2/print/qztray'

/** 라벨(반품처리)·바코드 재발행 두 규격(1.0 `RGRTemplate.js` 고정값, 8.1-9). */
export const LABEL_PRINTER_CONFIG = { printerName: 'BIXOLON SLP-DL410', widthIn: 3.5, heightIn: 4.5 } as const
export const BARCODE_PRINTER_CONFIG = { printerName: 'BARCODE', widthIn: 3, heightIn: 4.5 } as const

let securityConfigured = false

/** 인증서·서명 설정(1.0 `connectWithPrinter` 의 `qz.security.*` 부분). 한 번만 등록한다. */
const configureSecurity = (): void => {
  if (securityConfigured) return
  if (!QZ_TRAY_CERT || !QZ_TRAY_PRIVATE_KEY) {
    throw new Error('QZ Tray 인증정보가 설정되지 않았습니다.')
  }

  qz.security.setCertificatePromise((resolve) => resolve(QZ_TRAY_CERT))
  qz.security.setSignatureAlgorithm('SHA512')
  qz.security.setSignaturePromise((toSign: string) => (resolve, reject) => {
    try {
      const pk = KEYUTIL.getKey(QZ_TRAY_PRIVATE_KEY)
      const sig = new KJUR.crypto.Signature({ alg: 'SHA512withRSA' })
      sig.init(pk)
      sig.updateString(toSign)
      resolve(stob64(hextorstr(sig.sign())))
    } catch (err) {
      reject(String(err))
    }
  })

  securityConfigured = true
}

const ensureConnected = async (): Promise<void> => {
  configureSecurity()
  if (qz.websocket.isActive()) return
  await qz.websocket.connect({ port: { secure: [8181], insecure: [8182] }, usingSecure: true, retries: 0, keepAlive: 60, delay: 0 })
}

export interface PrintJob {
  printerName: string
  widthIn: number
  heightIn: number
  /** 인쇄할 영역의 `innerHTML`(1.0 과 같은 방식 — `type:'pixel', format:'html'`). */
  html: string
}

/**
 * qz-tray 로 HTML 영역을 인쇄한다.
 *
 * qz-tray 데스크톱 에이전트가 로컬에 없으면(미설치·미실행) `websocket.connect` 가 실패한다 —
 * 호출부가 이 실패를 삼켜 저장 흐름을 막지 않아야 한다(8.1-10). 이 함수 자체는 삼키지 않고 그대로
 * 던진다 — 삼키는 지점은 "저장 직후 자동 인쇄"만이고, 사용자가 직접 누르는 "바코드 인쇄"는 실패를
 * 알려야 한다(그래야 재시도할 수 있다).
 */
export const printHtml = async ({ printerName, widthIn, heightIn, html }: PrintJob): Promise<void> => {
  await ensureConnected()
  const config = qz.configs.create(printerName, { size: { width: widthIn, height: heightIn }, units: 'in', scaleContent: false })
  await qz.print(config, [{ type: 'pixel', format: 'html', flavor: 'plain', data: html }])
}
